import axios from 'axios';

import { API_BASE_URL } from '../../constants';

export const getDocumentApiError = (error) => {
    const data = error?.response?.data || {};
    const fileError = data?.errors?.file;
    const fileMessage = Array.isArray(fileError) ? fileError.join(' ') : fileError;
    let message = '';

    if (typeof data.message === 'string') {
        message = data.message;
    } else if (typeof data.message?.details?.error === 'string') {
        message = data.message.details.error;
    } else if (typeof data.error === 'string') {
        message = data.error;
    }

    return {
        status: error?.response?.status,
        message,
        fileMessage: fileMessage || '',
    };
};

const cleanParams = (params = {}) => Object.fromEntries(
    Object.entries(params).filter(([, value]) => (
        value !== undefined && value !== null && value !== ''
    ))
);

export default class DocumentManagementService {
    static instance;

    static getInstance() {
        if (!DocumentManagementService.instance) {
            DocumentManagementService.instance = new DocumentManagementService();
        }

        return DocumentManagementService.instance;
    }

    getHeaders(additionalHeaders = {}) {
        return {
            Authorization: `Bearer ${localStorage.getItem(
                'LOCAL_STORAGE_KEY_ACCESS_TOKEN'
            )}`,
            ...additionalHeaders,
        };
    }

    async list(params = {}) {
        const response = await axios.get(`${API_BASE_URL}/tp_document/index`, {
            params: cleanParams(params),
            headers: this.getHeaders(),
        });

        return response.data?.data || {};
    }

    async listBySupplier(supplierId, params = {}) {
        const response = await axios.get(
            `${API_BASE_URL}/supplier/${supplierId}/documents`,
            {
                params: cleanParams(params),
                headers: this.getHeaders(),
            }
        );

        return response.data?.data || {};
    }

    async listByProduct(productId, params = {}) {
        const response = await axios.get(
            `${API_BASE_URL}/product/${productId}/documents`,
            {
                params: cleanParams(params),
                headers: this.getHeaders(),
            }
        );

        return response.data?.data || {};
    }

    async create(payload) {
        const response = await axios.post(
            `${API_BASE_URL}/tp_document/create`,
            payload,
            {
                headers: this.getHeaders(),
            }
        );

        return response.data?.data;
    }

    async update(documentId, payload) {
        const response = await axios.post(
            `${API_BASE_URL}/tp_document/${documentId}/update`,
            payload,
            {
                headers: this.getHeaders({
                    'Content-Type': 'multipart/form-data',
                }),
            }
        );

        return response.data?.data;
    }

    async delete(documentId) {
        const response = await axios.delete(
            `${API_BASE_URL}/tp_document/${documentId}/delete`,
            {
                headers: this.getHeaders(),
            }
        );

        return response.data?.data;
    }

    async download(documentId, onDownloadProgress) {
        const response = await axios.get(
            `${API_BASE_URL}/tp_document/${documentId}/download`,
            {
                responseType: 'arraybuffer',
                headers: this.getHeaders(),
                onDownloadProgress,
            }
        );

        return response.data;
    }

    async startBulkUpload() {
        const response = await axios.post(
            `${API_BASE_URL}/tp_document/bulk_upload/start`,
            {},
            {
                headers: this.getHeaders(),
            }
        );

        return response.data?.data;
    }

    async uploadBulkFiles(jobId, files) {
        const formData = new FormData();

        files.forEach((file) => {
            formData.append('files[]', file);
        });

        const response = await axios.post(
            `${API_BASE_URL}/tp_document/bulk_upload/${jobId}/files`,
            formData,
            {
                headers: this.getHeaders(),
            }
        );

        return response.data?.data;
    }

    async finalizeBulkUpload(jobId) {
        const response = await axios.post(
            `${API_BASE_URL}/tp_document/bulk_upload/${jobId}/finalize`,
            {},
            {
                headers: this.getHeaders(),
            }
        );

        return response.data?.data;
    }

    async getBulkUploadStatus(jobId) {
        const response = await axios.get(
            `${API_BASE_URL}/tp_document/bulk_upload/${jobId}/status`,
            {
                headers: this.getHeaders(),
            }
        );

        return response.data?.data;
    }

    async linkResync(documentId) {
        const response = await axios.post(
            `${API_BASE_URL}/tp_document/${documentId}/link_resync`,
            {},
            {
                headers: this.getHeaders(),
            }
        );

        return response.data?.data;
    }
}
